/**
 * UnifiedRecalibrationProgress — single component for native + web.
 *
 * Apr 28, 2026 (Recal Hardening Sprint, PR 7, G19):
 *   Replaces the prior pair (web/src/components/RecalibrationProgress.tsx
 *   + the inline RecalibrationProgressNative inside
 *   web/src/screens/ChatScreen.native.tsx ~lines 836–1206). Drift
 *   between the two had become a known risk — features added on
 *   one side (ui_class branches, exhausted/onExhausted, refined
 *   onDismiss signature) lagged on the other.
 *
 * Design choices:
 *   - Built on React Native primitives (View, Text, ActivityIndicator,
 *     Pressable). RN-Web makes these render correctly on both web and
 *     native, which is exactly what `@/components/unified` is for.
 *   - Visual treatment matches the native cedarLight design (solid
 *     accent + white text). This is the more complete of the two
 *     prior visual designs (it has multi_stage / thin / failed
 *     variants); standardising removes drift permanently.
 *   - Navigation is injected (`onCtaPress`) so the component itself
 *     never imports react-router-dom or expo-router. Each platform's
 *     parent wires up its own router. This keeps the unified
 *     component renderer-agnostic.
 *   - Token resolution and active-plan-id resolution are also
 *     injected, for the same reason.
 */

import { isRecalPublicStatus, type RecalPublicStatus } from './recalStatusContract';
import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  ActivityIndicator,
  Animated,
  Easing,
  Pressable,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { makeStyles, onGlass, useTheme, withAlpha } from '../../theme';
import {
  advanceRecalProgress,
  initialRecalProgressState,
  type RecalProgressState,
  type RecalStagePlan,
} from './recalProgressBar';

export interface RecalibrationStatusData {
  tracking_id: string;
  // 'stalled' (2026-05-05, Task 5): synthetic status the backend returns
  // when status='running' but heartbeat_at is older than 120s. Treat
  // identically to failed+exhausted — the orchestrator that owned this row
  // isn't coming back, manual retry is the exit. The retry endpoint
  // accepts stalled rows directly (no need to wait for the cron sweep to
  // promote them to 'failed').
  //
  // 'dismissed' (2026-05-10, stuck-banner-fix): synthetic status returned
  // when user_dismissed_at IS NOT NULL on the tracking row. Takes
  // precedence over every other status. The hydration watcher treats
  // this like 'not_found' — skip mount + seed dedup ref.
  status: RecalPublicStatus;
  scope: string;
  label: string;
  substatus: string;
  started_at: number | null;
  completed_at: number | null;
  entity_id: string | null;
  entity_type: string | null;
  // Action UX Contract render variant (Apr 21, 2026).
  // 'multi_stage' default, 'thin' for brief actions, 'celebration'
  // for COMPLETE_GOAL, 'toast'/'none' should never reach this
  // component (no tracking row created upstream).
  ui_class?: 'multi_stage' | 'thin' | 'celebration' | 'toast' | 'none';
  // SR-RECAL retry surface (Phase 1.4).
  retry_count?: number;
  auto_retry_attempted?: boolean;
  last_failure_type?: string | null;
  error_message?: string | null;
  exhausted?: boolean;
  // 100%-closure (2026-06-10) — honest-progress fields (migration 1619,
  // populated by the dispatcher's UX contract + the verifiable-mutation
  // tick). stage_index advances only when a real DB mutation lands;
  // expected_duration_seconds is the action's calibrated estimate.
  stage_index?: number | null;
  stage_total?: number | null;
  expected_duration_seconds?: number | null;
  primary_action?: string | null;
  // 2026-09-17 (measured pacing, migration 1808) — served only when the
  // backend flag is on and the columns exist. The plan positions the bar; the
  // two clocks are server timestamps (epoch seconds); server_now lets the
  // client cancel its own clock skew. Absent → the legacy pacing path.
  progress_plan?: RecalStagePlan | null;
  substatus_changed_at?: number | null;
  dispatch_started_at?: number | null;
  server_now?: number | null;
}

export interface UnifiedRecalibrationProgressProps {
  trackingId: string;
  recalibrationType?: string;

  /**
   * Called when the strip should be torn down. Signature mirrors the
   * native parent's expectations:
   *   - succeeded: did the recalibration complete successfully
   *   - finalScope: live scope from the most recent poll (not
   *     mount-time placeholder)
   *   - finalEntityId: live entity_id from the most recent poll
   *   - finalUiClass: live ui_class — parent uses this to decide
   *     between completion pill (thin/multi_stage) and celebration
   *     card (celebration)
   *   - dismissReason: 'failed_dismiss' iff this dismissal originates
   *     from the user tapping Dismiss on the failed-state banner AFTER
   *     the component awaited a 2xx from POST /api/recalibration/
   *     dismiss/{tid}. Parents use this for observability (today: dev
   *     logging; future: analytics on which dismiss surface fired).
   *     The POST itself lives in the component — parents must NOT
   *     re-fire it. Omit for non-failed dismissals.
   * Web callers that don't care about the args can still pass a
   * zero-arg () => void handler; JS argument drop is safe.
   */
  onDismiss?: (
    succeeded: boolean,
    finalScope: string,
    finalEntityId: string | null,
    finalUiClass?: string,
    dismissReason?: 'failed_dismiss' | 'unknown_status',
  ) => void;

  /**
   * Fired exactly once when the auto-retry budget is spent
   * (status='failed' AND exhausted=true). Parent posts a
   * ChatErrorCard so the user sees the same Try Again / Contact
   * Coach Cedar Team buttons as a failed chat send.
   */
  onExhausted?: (trackingId: string) => void;

  /**
   * Caller-injected navigation. The component computes the route
   * via {@link recalCtaConfig} and hands it back. Web callers
   * pass `(route) => navigate(route, { state: {...} })`; native
   * callers pass `(route) => router.push(route as any)`.
   */
  onCtaPress?: (route: string, scope: string, entityId: string | null) => void;

  /**
   * Resolves the user's active plan id — used by the plan-detail
   * branches in {@link recalCtaConfig}. Native uses
   * NativeDataService.getActivePlanIdSync(); web has its own
   * resolver. Returning null falls through to a /(tabs)/mypath-style
   * route prefix (caller's responsibility).
   */
  resolveActivePlanId?: () => string | null;

  /**
   * Returns the user's auth token (or null/undefined). Native uses
   * secureTokenStorage.getCedarJwt(); web uses storage.getItem.
   */
  getAuthToken: () => Promise<string | null | undefined>;

  /** Backend base URL (e.g. https://api.coachcedar.com). */
  apiBaseUrl: string;

  /**
   * Optional override for the goal/multi_goal CTA route. Web's
   * legacy setting is /goal/{id}; native's is /(tabs)/mypath. The
   * caller picks. Falls back to /goal/{entity_id} when omitted.
   */
  goalRoute?: (entityId: string | null) => string;

  /**
   * Optional override for the workout-detail CTA route. Web is
   * /workout/{id}; native is /workout/{id}. Pass through unless
   * the platform diverges.
   */
  workoutRoute?: (entityId: string) => string;

  /**
   * Optional fallback when scope=plan/framework but no active
   * plan id is resolvable. Native points at the my-path tab; web
   * sends to a generic /plan route. Caller decides.
   */
  fallbackPlanRoute?: string;
}

/** Scope-aware CTA config: button label + navigation route for completed state */
function recalCtaConfig(
  scope: string,
  entityId: string | null,
  resolveActivePlanId: () => string | null,
  opts: {
    goalRoute?: (entityId: string | null) => string;
    workoutRoute?: (entityId: string) => string;
    fallbackPlanRoute?: string;
  },
): { label: string; route: string } | null {
  // 🗓️ APR 21, 2026 — Action UX Contract null branches.
  //   - goal_pending → no CTA (REDIRECT_TO_NEW_GOAL_PIPELINE; the plan
  //     hasn't changed, there's nothing to "view".)
  //   - none → no CTA (NO_ACTION; not a real recal turn)
  //   - unknown scope → no CTA (fail closed)
  const fallbackPlanRoute = opts.fallbackPlanRoute ?? '/(tabs)/mypath';
  const activePlanId = resolveActivePlanId();
  const planDetailRoute = activePlanId ? `/plan/${activePlanId}` : fallbackPlanRoute;
  const goalRouteFn = opts.goalRoute ?? ((id: string | null) => (id ? `/goal/${id}` : fallbackPlanRoute));
  const workoutRouteFn = opts.workoutRoute ?? ((id: string) => `/workout/${id}`);

  switch (scope) {
    case 'session':
      return entityId
        ? { label: 'View Workout', route: workoutRouteFn(entityId) }
        : { label: 'View Plan', route: planDetailRoute };
    case 'goal':
    case 'multi_goal':
      return { label: 'View Plan', route: goalRouteFn(entityId) };
    case 'plan':
    case 'framework':
      return { label: 'View Plan', route: planDetailRoute };
    case 'goal_pending':
    case 'none':
      return null;
    default:
      return null;
  }
}

const MAX_FAILURES = 40;
const POLL_INTERVAL_MS = 3000;

export const UnifiedRecalibrationProgress: React.FC<UnifiedRecalibrationProgressProps> = ({
  trackingId,
  recalibrationType,
  onDismiss,
  onExhausted,
  onCtaPress,
  resolveActivePlanId,
  getAuthToken,
  apiBaseUrl,
  goalRoute,
  workoutRoute,
  fallbackPlanRoute,
}) => {
  // The theme's two hooks come first: every guard in this component is below every hook.
  const styles = useStyles();
  const { palette } = useTheme();
  const [statusData, setStatusData] = useState<RecalibrationStatusData | null>(null);
  const [polling, setPolling] = useState(true);

  // 2026-05-10 (stuck-banner-fix): failed-state Dismiss owns its own POST
  // to /api/recalibration/dismiss/{tid}. Local state surfaces in-flight
  // and error states so the user sees the truth — no optimistic local
  // hide before server confirmation, no silent failure, no fire-and-
  // forget. On POST success the strip is torn down via onDismiss
  // (existing sync API). On POST failure the strip stays mounted, the
  // Dismiss button re-enables, and an inline error message tells the
  // user to try again.
  const [isDismissing, setIsDismissing] = useState(false);
  const [dismissError, setDismissError] = useState<string | null>(null);

  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const onExhaustedRef = useRef(onExhausted);
  onExhaustedRef.current = onExhausted;
  const exhaustedFiredRef = useRef(false);
  const completedFiredRef = useRef(false);

  const failureCountRef = useRef(0);

  useEffect(() => {
    if (!polling) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const token = await getAuthToken();
        if (!token || cancelled) return;
        const res = await fetch(`${apiBaseUrl}/api/recalibration/status/${trackingId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!res.ok) {
          failureCountRef.current += 1;
          if (failureCountRef.current >= MAX_FAILURES) {
            setPolling(false);
            setStatusData(prev =>
              prev
                ? { ...prev, status: 'failed' }
                : {
                    tracking_id: trackingId,
                    status: 'failed',
                    scope: '',
                    label: '',
                    substatus: '',
                    started_at: null,
                    completed_at: null,
                    entity_id: null,
                    entity_type: null,
                  },
            );
          }
          return;
        }
        failureCountRef.current = 0;
        const data: RecalibrationStatusData = await res.json();
        if (cancelled) return;
        setStatusData(data);
        if (data.status === 'completed' || data.status === 'not_found') {
          setPolling(false);
          if (data.status === 'not_found') {
            setStatusData(prev => ({ ...(prev || data), status: 'completed' }));
          }
        } else if (data.status === 'awaiting_input' || data.status === 'dismissed') {
          // 🧭 2026-09-16 (Consent Seams T2): a parked question is not work.
          // The chat bubble already asked it; stop polling and unmount
          // quietly (owner D2) — no completion, no failure, no toast.
          setPolling(false);
          onDismissRef.current?.(false, data.scope || '', data.entity_id ?? null, data.ui_class);
        } else if (data.status === 'failed' || data.status === 'stalled') {
          // SR-RECAL Phase 1.4: only stop polling when auto-retry is
          // exhausted (or when the backend is older than Phase 1.4 and
          // doesn't surface the field — fall back to legacy stop-on-fail
          // so we don't strand a spinner).
          //
          // 2026-05-05 (Task 5): stalled rows arrive with exhausted=true
          // unconditionally (heartbeat-derived dead recals always exit via
          // the manual retry path), so the same gate handles both terminal
          // surfaces. Stop polling either way — once a recal is stalled,
          // the next state change will only come from the user's Retry
          // click.
          if (data.exhausted === true) {
            setPolling(false);
            if (!exhaustedFiredRef.current) {
              exhaustedFiredRef.current = true;
              onExhaustedRef.current?.(trackingId);
            }
          } else if (data.exhausted === undefined) {
            setPolling(false);
          }
          // exhausted === false: silent retry in flight, keep polling.
        } else if (!isRecalPublicStatus(data.status)) {
          // 🧭 2026-09-16 (Consent Seams T2, class-kill): a status nobody
          // defined must never render as in-progress forever. Cory's
          // "Still working — 182m 38s" was exactly this: the server sent
          // awaiting_user_input to a client that only knew six statuses.
          // A contract server maps unknowns to 'dismissed'; this is the belt
          // for an older or flag-off server. The 'unknown_status' reason tells
          // the parent to seed its dedup so the carrier cannot re-mount.
          setPolling(false);
          onDismissRef.current?.(false, data.scope || '', data.entity_id ?? null, data.ui_class, 'unknown_status');
        }
      } catch {
        if (cancelled) return;
        failureCountRef.current += 1;
        if (failureCountRef.current >= MAX_FAILURES) {
          setPolling(false);
          setStatusData(prev =>
            prev
              ? { ...prev, status: 'failed' }
              : {
                  tracking_id: trackingId,
                  status: 'failed',
                  scope: '',
                  label: '',
                  substatus: '',
                  started_at: null,
                  completed_at: null,
                  entity_id: null,
                  entity_type: null,
                },
          );
        }
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [trackingId, polling, apiBaseUrl, getAuthToken]);

  // Elapsed counter — runs while polling.
  //
  // ⚠️ ANCHORED TO THE SERVER'S started_at, NOT TO MOUNT. This clock used to
  // read `Date.now() - mountTimeRef`, while `started_at` was already on the
  // wire (routers/recalibration_status.py:345), already typed on this very
  // interface, and read by nothing. So every remount — backgrounding the app,
  // navigating away and back — restarted the clock at zero and replayed the
  // whole progress story from the top on a run that was nearly done. That is
  // the larger half of the founder's "inaccurate" report (2026-09-02); the
  // p50 constant was only the smaller half.
  //
  // Mount time survives as the pre-first-poll fallback: for the ~1s before the
  // first response lands there is no server anchor to use, and 0 is correct
  // then anyway.
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const mountTimeRef = useRef(Date.now());
  const startedAt = statusData?.started_at ?? null;
  useEffect(() => {
    if (!polling) return;
    const anchorMs = startedAt != null ? startedAt * 1000 : mountTimeRef.current;
    const tick = () => setElapsedSeconds(Math.max(0, Math.floor((Date.now() - anchorMs) / 1000)));
    tick(); // don't wait a full second to correct a stale mount-anchored value
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [polling, startedAt]);

  const isComplete = statusData?.status === 'completed';
  const isExhausted = statusData?.exhausted === true;
  const isLegacyFailedNoExhaustedField =
    statusData?.status === 'failed' && statusData?.exhausted === undefined;
  const isFailed = isExhausted || isLegacyFailedNoExhaustedField;

  // Auto-dismiss on success — parent wipes the strip. Pass live values
  // so the parent computes its completion artifact with the
  // backend-refined scope, not the mount-time placeholder.
  useEffect(() => {
    if (isComplete && !completedFiredRef.current) {
      completedFiredRef.current = true;
      // 🩺 MAY 12 2026 (Synchronized Anchor Club, U2) — publish a
      // completion event on the recalCompletionBus so other screens
      // (MyPath, WorkoutDetail) can refresh their cached data
      // immediately. The chat-side onDismiss continues to insert the
      // completion pill; this bus emit fans the same signal out to
      // screens that aren't this widget's parent.
      try {
        const { publishRecalCompletion } = require('../../services/recalCompletionBus');
        publishRecalCompletion(
          trackingId,
          statusData?.scope || '',
          statusData?.entity_id || null,
          statusData?.ui_class,
        );
      } catch (e) {
        // Bus failure must never block the dismiss flow.
        console.warn('[RecalibrationProgress] recalCompletionBus publish failed:', e);
      }
      onDismissRef.current?.(
        true,
        statusData?.scope || '',
        statusData?.entity_id || null,
        statusData?.ui_class,
      );
    }
  }, [isComplete, statusData?.scope, statusData?.entity_id, statusData?.ui_class, trackingId]);

  const baseLabel = statusData?.label || recalibrationType || 'Updating your plan';
  // 2026-06-10 (Paul): the "taking a bit longer" flip was a FIXED 90s —
  // but a full week rebuild measures p50 ~10min, so the scare copy showed
  // for ~9 minutes on EVERY normal run. The threshold is now relative to
  // the action's calibrated estimate (1.5× expected) when the backend
  // provides one; 90s remains the fallback for actions with no estimate.
  // Inside the estimate window the strip stays on the normal label + the
  // honest countdown — slow-warning copy is reserved for genuinely
  // anomalous runs.
  const slowExpected = statusData?.expected_duration_seconds;
  const slowThreshold =
    slowExpected != null && slowExpected > 0
      ? Math.max(90, Math.round(slowExpected * 1.5))
      : 90;
  const statusLabel =
    elapsedSeconds > slowThreshold && !isComplete && !isFailed
      ? 'Still working — taking a bit longer than usual'
      : baseLabel;
  // Duration formatter — long phases read as minutes, not raw seconds
  // ("8m 24s", never "612s"). This is ELAPSED, which is a fact.
  //
  // Its counterpart `fmtEta` is deliberately gone (founder 2026-09-02).
  // "~10m left" was `expected_duration_seconds - elapsedSeconds`, and
  // expected_duration_seconds is a per-ACTION POPULATION CONSTANT — 600 for a
  // week replan, measured p50=603/p90=650. It is a good average and a category
  // error as a per-run promise: by construction ~half of all runs blow through
  // it to zero, at which point the countdown silently vanished and the label
  // flipped to "taking a bit longer than usual". The number is still used — it
  // paces the status bar's creep — but it is no longer SHOWN as a promise.
  const fmtDur = (s: number): string =>
    s > 90 ? `${Math.floor(s / 60)}m${s % 60 ? ` ${s % 60}s` : ''}` : `${s}s`;
  const cta = isComplete
    ? recalCtaConfig(
        statusData?.scope || '',
        statusData?.entity_id || null,
        resolveActivePlanId ?? (() => null),
        { goalRoute, workoutRoute, fallbackPlanRoute },
      )
    : null;

  const handleCtaPress = () => {
    if (cta && onCtaPress) {
      onCtaPress(cta.route, statusData?.scope || '', statusData?.entity_id || null);
    }
    onDismissRef.current?.(false, statusData?.scope || '', statusData?.entity_id || null);
  };

  // Action UX Contract render variant. 'toast' / 'none' should never
  // reach this component — the backend doesn't create tracking rows
  // for those actions — but if a stale row arrives, fail closed.
  // The early return that ACTS on this lives below the last hook (see the
  // RULES OF HOOKS note there). `uiClass` is computed here because the
  // paused-variant logic and the render helpers below both read it.
  const uiClass = statusData?.ui_class || 'multi_stage';

  // 🩺 MAY 12 2026 (Synchronized Anchor Club, U1 + U3) — Paused variant.
  // When the orchestrator stamps substatus='clarification_needed' (because
  // the reasoner emitted a question-class speaking_intent — see
  // chat_recalibration_orchestrator.py U3 hook), the pill switches from
  // its "inflight / working" treatment to a "paused / asking a question"
  // treatment. The user is now staring at a row that's honest about
  // what's happening: Cedar is waiting on their input, not updating the
  // plan.
  //
  // p.rush12 incident 2026-05-11 20:16–20:19 EDT: for 2 min 39 s the
  // pill said "Updating plan…" while Cedar was actually asking "can
  // you restate the change?" — U3+U1 close that gap.
  const isPaused = statusData?.substatus === 'clarification_needed';
  const pausedLabel = 'Asking a question…';

  // ── The status bar (founder 2026-09-02: "instead of an inaccurate time
  //    count down when you recal, we should have a status bar … then smooth
  //    the bar out. Similar to how the liquid loader for canva upload works")
  //
  // Layer 1 (truth) lives in ./recalProgressBar — read its header before
  // changing anything here, especially the part about why stage_index is NOT
  // the anchor it looks like. Layers 2 and 3 are below: an eased follower so a
  // stage landing glides instead of teleporting, and a shimmer so a bar that
  // is momentarily static still reads as alive.
  const progressStateRef = useRef<RecalProgressState>(initialRecalProgressState());
  const progressAnim = useRef(new Animated.Value(0)).current;
  const shimmerAnim = useRef(new Animated.Value(0)).current;
  const [trackWidth, setTrackWidth] = useState(0);

  // 2026-09-17 (measured pacing) — every elapsed the bar draws is measured on
  // the SERVER's clock: (server_now + the local delta since that poll) minus a
  // server timestamp. A phone whose clock is a minute fast can no longer put
  // the bar a minute ahead of the run.
  const serverOffsetRef = useRef<number | null>(null);
  const serverNow = statusData?.server_now;
  if (serverNow != null && Number.isFinite(serverNow)) {
    serverOffsetRef.current = serverNow - Date.now() / 1000;
  }

  useEffect(() => {
    const offset = serverOffsetRef.current;
    const nowServer = offset != null ? Date.now() / 1000 + offset : null;
    const dispatchStartedAt = statusData?.dispatch_started_at;
    const substatusChangedAt = statusData?.substatus_changed_at;
    // The bar's clock is the DISPATCH clock when the server provides one. The
    // row's started_at (behind `elapsedSeconds`) is the confirm turn's reasoner
    // start, 14–43 s before any stage exists.
    const dispatchElapsed =
      nowServer != null && dispatchStartedAt != null && Number.isFinite(dispatchStartedAt)
        ? Math.max(0, nowServer - dispatchStartedAt)
        : null;
    const stageElapsed =
      nowServer != null && substatusChangedAt != null && Number.isFinite(substatusChangedAt)
        ? Math.max(0, nowServer - substatusChangedAt)
        : null;
    const next = advanceRecalProgress(progressStateRef.current, {
      primaryAction: statusData?.primary_action ?? null,
      substatus: statusData?.substatus ?? null,
      stageIndex: statusData?.stage_index ?? null,
      stageTotal: statusData?.stage_total ?? null,
      elapsedSeconds: dispatchElapsed ?? elapsedSeconds,
      expectedSeconds: statusData?.expected_duration_seconds ?? null,
      status: statusData?.status ?? null,
      stagePlan: statusData?.progress_plan ?? null,
      stageElapsedSeconds: stageElapsed,
    });
    progressStateRef.current = next;
    // ⚠️ useNativeDriver MUST stay false — width is a layout prop and the
    // native driver cannot animate it. (OnboardingProgressNative uses true
    // only because it animates scale/opacity.)
    Animated.timing(progressAnim, {
      toValue: next.fraction,
      duration: 650,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [
    progressAnim,
    elapsedSeconds,
    statusData?.primary_action,
    statusData?.substatus,
    statusData?.stage_index,
    statusData?.stage_total,
    statusData?.expected_duration_seconds,
    statusData?.status,
    statusData?.progress_plan,
    statusData?.substatus_changed_at,
    statusData?.dispatch_started_at,
  ]);

  // The shimmer runs off the truth layer entirely — that is its whole job.
  useEffect(() => {
    if (isPaused || trackWidth <= 0) return;
    shimmerAnim.setValue(0);
    const loop = Animated.loop(
      Animated.timing(shimmerAnim, {
        toValue: 1,
        duration: 1600,
        easing: Easing.inOut(Easing.quad),
        useNativeDriver: true,
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [shimmerAnim, isPaused, trackWidth]);

  // Action UX Contract render variant: 'toast' / 'none' should never reach
  // this component — the backend doesn't create tracking rows for those
  // actions — but if a stale row arrives, fail closed.
  //
  // RULES OF HOOKS: this guard MUST stay below every hook call. It used to
  // sit ~80 lines up, ABOVE the six progress-bar hooks added 2026-09-02
  // (useRef x3, useState, useEffect x2). `statusData` is re-polled live
  // while this strip is mounted, so a tracking row whose `ui_class` changed
  // underneath it would swing the hook count by six and throw
  // "Rendered more hooks than during the previous render" — the same defect
  // that blanked WorkoutDetail via DebriefButton (fixed in the same commit).
  // Never hoist this above a hook.
  if (uiClass === 'none' || uiClass === 'toast') {
    return null;
  }

  const renderProgressBar = (variant: 'full' | 'thin') => (
    <View
      style={variant === 'thin' ? styles.progressTrackThin : styles.progressTrack}
      onLayout={(e) => {
        const w = Math.round(e.nativeEvent.layout.width);
        if (w > 0 && w !== trackWidth) setTrackWidth(w);
      }}
      // The bar restates the caption beside it; announcing both makes VoiceOver
      // say the same stage twice.
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      <Animated.View
        style={[
          styles.progressFill,
          { width: progressAnim.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }) },
        ]}
      >
        {trackWidth > 0 ? (
          <Animated.View
            style={[
              styles.progressShimmer,
              {
                width: SHIMMER_WIDTH,
                transform: [
                  {
                    translateX: shimmerAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [-SHIMMER_WIDTH, trackWidth],
                    }),
                  },
                ],
              },
            ]}
            pointerEvents="none"
          />
        ) : null}
      </Animated.View>
    </View>
  );

  // ── Running state ──────────────────────────────────────────────────────
  if (!isComplete && !isFailed) {
    // 🩺 2026-06-03 (recalibration-confirm-first, Phase 3b) — decommission the
    // "Waiting for your reply" pill. When Cedar resolves to a question
    // (substatus='clarification_needed'), the ball is in the user's court and
    // the chat bubble already carries the question — so dismiss the pill rather
    // than show a redundant progress indicator (with a counting-up timer) over
    // a question the user can already see. The working/executing pill below is
    // untouched (that's the Brian-class silent-wait fix and stays). Kill-switch
    // is a code revert of this early return.
    if (isPaused) {
      return null;
    }
    if (uiClass === 'thin') {
      return (
        <View
          style={[
            styles.thinContainer,
            isPaused && styles.thinContainerPaused,
          ]}
          accessible={true}
          accessibilityLabel={
            isPaused
              ? `Paused. ${baseLabel}. Coach Cedar is asking you a question.`
              : `${statusLabel}. Working on it.`
          }
          accessibilityRole="progressbar"
          accessibilityLiveRegion="polite"
        >
          {isPaused ? (
            <Text style={styles.pausedGlyph}>⏸</Text>
          ) : (
            <ActivityIndicator
              size="small"
              color={onGlass(0.85)}
              style={styles.spinnerInline}
            />
          )}
          <Text style={styles.thinLabel} numberOfLines={1}>
            {isPaused ? pausedLabel : statusLabel}
            {!isPaused && statusData?.substatus ? ` · ${statusData.substatus}` : ''}
          </Text>
          {/* Elapsed only. Never an ETA — see fmtDur's note. */}
          {elapsedSeconds > 3 && (
            <Text style={styles.thinElapsed}>{fmtDur(elapsedSeconds)}</Text>
          )}
          {/* thinContainer is a ROW, so the bar rides its bottom edge as an
              absolute child instead of trying to stack inside the row. Yoga
              measures an inset-positioned absolute child from the PADDING BOX,
              so left/right/bottom 0 lands it flush inside the hairline —
              the same geometry the ramp above relies on. */}
          {!isPaused ? renderProgressBar('thin') : null}
        </View>
      );
    }

    return (
      <View
        style={[
          styles.container,
          isPaused && styles.containerPaused,
        ]}
        accessible={true}
        accessibilityLabel={
          isPaused
            ? `Paused. ${baseLabel}. Coach Cedar is asking you a question.`
            : `${statusLabel}. Working on it.`
        }
        accessibilityRole="progressbar"
        accessibilityLiveRegion="polite"
      >
        {/* Not while paused: that state repaints the card amber and a ramp
            child would paint straight over the signal. */}
        {!isPaused ? <PillRamp /> : null}
        <Text style={styles.title}>{isPaused ? pausedLabel : statusLabel}</Text>
        <View style={styles.statusRow}>
          {isPaused ? (
            <Text style={styles.pausedGlyph}>⏸</Text>
          ) : (
            <ActivityIndicator
              size="small"
              color={onGlass(0.8)}
              style={styles.spinnerInline}
            />
          )}
          <Text style={styles.statusLabel}>
            {isPaused
              ? 'Waiting for your reply'
              : (statusData?.substatus || 'Working on it…')}
            {/* 2026-09-17: the "step N of M" caption is gone (founder). It read
                "step 1 of 3" only in the final two seconds of a replan —
                stage_index ticks ONCE, after the mutation lands, and
                stage_total is written before the stage list is replaced —
                so it was misleading by construction. The bar carries
                progress; the caption carries the stage's words. */}
          </Text>
          {elapsedSeconds > 3 && (
            <Text style={styles.statusElapsed}>{fmtDur(elapsedSeconds)}</Text>
          )}
        </View>
        {/* The status bar replaces the countdown that used to sit where the
            elapsed reading now is (founder 2026-09-02). Not rendered while
            paused: that state repaints the card amber to say the ball is in
            the user's court, and a bar creeping underneath would contradict
            it — nothing is progressing. */}
        {!isPaused ? renderProgressBar('full') : null}
      </View>
    );
  }

  // Completed state: parent renders a completion pill in the chat feed
  // via onDismiss(true, ...). Component returns null so it doesn't
  // double-up with the parent's pill.
  if (isComplete) {
    return null;
  }

  // ── Failed state ───────────────────────────────────────────────────────
  // 2026-05-10 (stuck-banner-fix): Dismiss is server-canonical.
  //
  // Flow: tap → button enters dismissing state (disabled + spinner) →
  // POST /api/recalibration/dismiss/{tid} (await) → on 2xx, fire
  // onDismiss (parent tears down the strip; component about to unmount,
  // so we don't reset isDismissing) → on non-2xx or thrown, surface
  // inline error and re-enable the button so the user can retry.
  //
  // Why no fire-and-forget: the dismissal IS the POST. Local-only hide
  // would lie to the user when the server didn't actually record it; on
  // next /status poll the banner would return as a delayed surprise.
  //
  // Why no AsyncStorage backup: the AsyncStorage cache used by the
  // success path was the original bug locus — extending it to failures
  // would re-introduce the same client-side drift across reinstall and
  // multi-device. Server is canonical.
  //
  // Idempotent POST means double-tap is harmless even if the disabled
  // state somehow races; the disabled gate exists for UX clarity, not
  // correctness.
  const handleDismissPress = async () => {
    if (isDismissing) return;
    setIsDismissing(true);
    setDismissError(null);

    let token: string | null | undefined = null;
    try {
      token = await getAuthToken();
    } catch (tokenErr) {
      console.warn(
        `[RECAL_DISMISS_POST_FAILED] tracking=${trackingId.substring(0, 8)} reason=token_resolver_threw`,
        tokenErr,
      );
      setIsDismissing(false);
      setDismissError('auth');
      return;
    }
    if (!token) {
      // No JWT means no authenticated user. The strip shouldn't be
      // visible in this state, but if it is, surface honestly rather
      // than silently no-op.
      console.warn(
        `[RECAL_DISMISS_POST_FAILED] tracking=${trackingId.substring(0, 8)} reason=no_jwt`,
      );
      setIsDismissing(false);
      setDismissError('auth');
      return;
    }

    try {
      const res = await fetch(
        `${apiBaseUrl}/api/recalibration/dismiss/${trackingId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!res.ok) {
        console.warn(
          `[RECAL_DISMISS_POST_FAILED] tracking=${trackingId.substring(0, 8)} status=${res.status}`,
        );
        setIsDismissing(false);
        setDismissError('http');
        return;
      }
    } catch (postErr) {
      console.warn(
        `[RECAL_DISMISS_POST_FAILED] tracking=${trackingId.substring(0, 8)} threw=${(postErr as Error)?.message ?? postErr}`,
      );
      setIsDismissing(false);
      setDismissError('network');
      return;
    }

    // POST 2xx — server is now canonical. Tear down the strip via the
    // existing sync onDismiss API. The 'failed_dismiss' marker is still
    // passed for parent observability (logging, future routing) even
    // though the parent no longer fires its own POST.
    onDismissRef.current?.(
      false,
      statusData?.scope || '',
      statusData?.entity_id || null,
      statusData?.ui_class,
      'failed_dismiss',
    );
    // Don't reset isDismissing — component about to unmount.
  };

  const failedSubtext =
    dismissError === null
      ? "Let Coach Cedar know what you'd like to change"
      : dismissError === 'auth'
      ? "We couldn't verify your session. Reload the app and try again."
      : "Couldn't reach the server. Tap Dismiss to try again.";

  return (
    <View
      style={styles.container}
      accessible={true}
      accessibilityLabel="Something unexpected happened. Let Coach Cedar know what you'd like to change."
      accessibilityLiveRegion="assertive"
    >
      <PillRamp />
      <View style={styles.failedRow}>
        <View style={styles.failedTextWrap}>
          <Text style={styles.failedText}>Something unexpected happened</Text>
          <Text style={styles.failedSubtext}>{failedSubtext}</Text>
        </View>
        <Pressable
          onPress={handleDismissPress}
          disabled={isDismissing}
          style={({ pressed }) => [
            styles.ctaButton,
            pressed && { opacity: 0.85 },
            isDismissing && { opacity: 0.6 },
          ]}
          accessibilityLabel="Dismiss error"
          accessibilityRole="button"
          accessibilityState={{ disabled: isDismissing, busy: isDismissing }}
        >
          {isDismissing ? (
            <ActivityIndicator size="small" color={palette.ink.onFill} />
          ) : (
            <Text style={styles.ctaButtonText}>Dismiss</Text>
          )}
        </Pressable>
      </View>
    </View>
  );
};

/**
 * The pill's ramp: light crest, deepening foot (see PILL_GRADIENT).
 *
 * ⚠️ FILLS THE BORDER BOX EXACTLY. It ships as absoluteFill and MUST NOT be
 * given negative insets again. #3210 gave it top/bottom -14, left/right -16 on
 * the stated premise that "Yoga insets an absolute child by the parent's
 * padding". That premise is FALSE for an inset-positioned child: Yoga measures
 * it from the parent's PADDING BOX (inside the border, padding NOT added), so
 * absoluteFill already covers the container's 14/16 padding and the negative
 * insets were a straight 14/16pt overpaint on every side, unclipped.
 *
 * The founder caught it on device (2026-09-02, "the pills are no longer fixed
 * above the chat composer"): the strip mounts inside a wrapper that pads 16
 * horizontally, so left:-16 put the gradient exactly on the screen edge — the
 * pill read as a full-bleed square-cornered slab, and its -14 foot bled down
 * under the composer, which paints after it and covered the overhang.
 *
 * The geometry is self-proving in both directions. Had padding been added,
 * -16 would have cancelled to the container's own edge and the pill would have
 * stayed inset; it did not. And `borderRadius: 5` — "GLASS_CARD's 6 minus its
 * 1px border" — only describes a concentric fit at inset 0; on a box 32pt
 * wider than the card those corners are off-screen entirely.
 *
 * The same false premise is written into CtaGradient.native.tsx (BLEED = 24).
 * It is HARMLESS there and is deliberately left alone: every CTA that mounts it
 * sets overflow:'hidden', so the bleed is clipped back and the site is correct
 * under either reading of Yoga. Only an UNCLIPPED consumer can be wrong — and
 * this is the only unclipped one, because it cannot clip: a view that clips
 * sets masksToBounds and eats its shadow, and this container's shadow comes
 * from GLASS_CARD, so overflow:'hidden' here would flatten every chat pill in
 * the app. Fill the box; do not bleed and hope.
 */
function PillRamp() {
  const styles = useStyles();
  const { palette } = useTheme();
  return (
    <LinearGradient
      colors={[...palette.glass.pillGradient.colors]}
      start={palette.glass.pillGradient.start}
      end={palette.glass.pillGradient.end}
      style={styles.ramp}
      pointerEvents="none"
    />
  );
}

/** The sweeping highlight's width. Wide enough to read as a sheen crossing a
 *  bar rather than a dot travelling along one. */
const SHIMMER_WIDTH = 96;

// An instrument: dark cedar glass under the pill's ramp, with white words. The same in both themes.
const useStyles = makeStyles((t) => ({
  container: {
    ...t.glass.card,
    padding: 14,
    paddingHorizontal: 16,
  },
  // Fills the container's border box exactly — no bleed (see PillRamp).
  // Radius 5 = GLASS_CARD's 6 minus its 1px border, so the ramp sits just
  // inside the hairline instead of over it.
  // Insets are written out rather than spread from StyleSheet.absoluteFillObject
  // on purpose. This geometry is the subject of the bug above, so it should be
  // readable at the site — and the jest RN mock stubs absoluteFillObject as an
  // EMPTY OBJECT (web/__mocks__/react-native.ts:29), so a spread would vanish
  // under test and no render gate could ever see the insets it exists to pin.
  ramp: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, borderRadius: 5 },
  // ── The status bar. Sits ON the pill's ramp, so the track is a dark well
  //    cut into it and the fill is the light. Both clip: the fill so the
  //    shimmer cannot escape it, the track so the fill's own corners stay
  //    inside the well at 0%.
  progressTrack: {
    height: 4,
    borderRadius: 2,
    backgroundColor: withAlpha(t.shadow.deep, 0.28),
    overflow: 'hidden',
    marginTop: 10,
  },
  // Inset from all three edges rather than flush to them. The card's radius is
  // 6, so a square-cornered bar pinned to left:0/right:0/bottom:0 would run
  // straight through both bottom corner arcs. The container cannot clip it
  // back — overflow:'hidden' would eat GLASS_CARD's shadow, the same
  // constraint the ramp above lives under — and per-corner radii are the wrong
  // instrument on a view that clips its own child (they smudge the arc). So
  // the bar floats INSIDE the corner arcs and carries one uniform radius.
  progressTrackThin: {
    position: 'absolute',
    left: 8,
    right: 8,
    bottom: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: withAlpha(t.shadow.deep, 0.28),
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    // The track clips, so this only shows at the leading edge — but at 0-2%
    // width a square corner is exactly what is visible. Matches the full
    // track's radius; the thin track's 1.5 is close enough that the 0.5pt
    // difference is sub-pixel at any real density.
    borderRadius: 2,
    backgroundColor: onGlass(0.92),
    overflow: 'hidden',
  },
  progressShimmer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    // A flat translucent band, not a gradient: the web build stubs
    // expo-linear-gradient down to its FIRST colour as a solid fill
    // (web/src/stubs/expo-linear-gradient.web.ts), so a gradient sweep would
    // silently become a hard-edged block there. One material, both platforms.
    backgroundColor: onGlass(0.55),
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
    color: t.ink.onFill,
    letterSpacing: -0.2,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
  },
  statusLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: onGlass(0.9),
  },
  statusElapsed: {
    fontSize: 12,
    color: t.glass.textSecondary,
    marginLeft: 8,
  },
  spinnerInline: {
    marginRight: 8,
  },
  thinContainer: {
    ...t.glass.card,
    paddingVertical: 8,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  thinLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: t.ink.onFill,
    flex: 1,
    letterSpacing: -0.1,
  },
  thinElapsed: {
    fontSize: 11,
    fontWeight: '500',
    color: onGlass(0.65),
    marginLeft: 8,
  },
  // 🩺 U1 paused variant (Synchronized Anchor Club, 2026-05-12).
  // Amber/warm-yellow background distinguishes "Cedar is waiting on
  // your input" from the cedarLight "working on it" treatment.
  // Activated when statusData.substatus === 'clarification_needed'.
  // NOT DRAWN TODAY: since 2026-06-03 the strip is taken down while it is paused (the early
  // `return null` above), and these three are that decision's way back. White on this amber is
  // 2.79:1 — pinned in theme/__tests__/palette.contrast.test.ts.
  containerPaused: {
    backgroundColor: t.glass.warnFill,  // amber — distinct from cedarLight running state
  },
  thinContainerPaused: {
    backgroundColor: t.glass.warnFill,
  },
  pausedGlyph: {
    fontSize: 16,
    color: t.ink.onFill,
    marginRight: 8,
    fontWeight: '600',
  },
  failedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  failedTextWrap: {
    flex: 1,
    marginRight: 12,
  },
  failedText: {
    fontSize: 14,
    fontWeight: '500',
    color: onGlass(0.85),
  },
  failedSubtext: {
    fontSize: 12,
    color: t.glass.textSecondary,
    marginTop: 2,
  },
    // Primary action: takes the primary fill. It was cedarLight with a
    // white label — 3.00:1, under the 4.5:1 AA floor. cedarPrime is 6.33:1.
  ctaButton: {
    backgroundColor: t.fill.brand,
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    ...t.glass.edge,
  },
  ctaButtonText: {
    color: t.ink.onFill,
    fontSize: 14,
    fontWeight: '600',
  },
}));
